import { HttpResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { camelCase } from '@recruitee/utils';

interface ConvertOptions {
  ignoreProperties?: string[];
}

export interface DraggableElement {
  position: number;
}

export const stripHtmlAndWhiteSpaces = (html: string): string =>
  Helpers.stripHtml(html)
    .replace(/\s+?/g, '')
    .replace(/&nbsp;/g, '');

// @dynamic - annotation for rollup compiler
export class Helpers {
  public static padZero(number: number): string {
    if (number < 10) {
      return `0${number}`;
    } else {
      return `${number}`;
    }
  }

  public static camelizeKeys(data: any, options?: ConvertOptions): any {
    return this.processKeys(this.camelize, data, options);
  }

  public static underscoreKeys(data: any, options?: ConvertOptions): any {
    return this.processKeys(this.underscore, data, options);
  }

  public static jsonize(data: any, options?: ConvertOptions): string {
    return JSON.stringify(this.underscoreKeys(data, options));
  }

  public static parseResponse(response: HttpResponse<any>): any {
    return response.body;
  }

  public static fillInReferences(items: any, references: any[]): any {
    const referencesTypes: string[] = [];
    const referencesById: Record<string, any> = {};
    (references || []).forEach(reference => {
      const referenceType = camelCase(reference.type);
      const referenceId = reference.id;
      referencesById[`${referenceType}_${referenceId}`] = reference;
      referencesTypes.push(referenceType);
    });

    return this.recursiveFillInReferences(items, referencesById, [...new Set(referencesTypes)]);
  }

  public static camelize(value: string): string {
    return value.replace(/[_]+(.)?/g, (match, chr) => {
      if (chr) {
        return chr.toUpperCase();
      } else {
        return '';
      }
    });
  }

  public static stripHtmlAndWhiteSpaces(html: string): string {
    return Helpers.stripHtml(html)
      .replace(/\s+?/g, '')
      .replace(/&nbsp;/g, '');
  }

  /**
   * @deprecated
   */
  public static encodeHTML(value: string): string {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  public static stripHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  public static underscore(value: string): string {
    return value
      .replace(/\W+/g, '_')
      .replace(/([a-z\d])([A-Z])/g, '$1_$2')
      .toLowerCase();
  }

  public static insertIntoDraggable<T extends DraggableElement>(newElement: T, elements: T[]): T[] {
    const firstHalf = elements.slice(0, newElement.position - 1);
    const secondHalf = elements.slice(newElement.position - 1);
    const newElements = [...firstHalf, newElement, ...secondHalf].map(
      (item, index) =>
        <T>{
          ...(<object>item),
          position: index + 1,
        },
    );

    return newElements;
  }

  public static getDateSort(asc: boolean = true): (a: Date, b: Date) => number {
    return (a, b) => (asc ? a.getTime() - b.getTime() : b.getTime() - a.getTime());
  }

  public static findRouteParamValue(state: RouterStateSnapshot, param: string): string | null {
    let route: ActivatedRouteSnapshot | null = state?.root || null;
    let paramValue!: string;
    while (route && !paramValue) {
      paramValue = route.params[param];
      route = route.firstChild;
    }
    return paramValue;
  }

  private static processKeys(
    convert: (value: string) => string,
    data: any,
    options?: ConvertOptions,
  ): any {
    if (
      !(typeof data === 'object' && data !== null) ||
      data instanceof Date ||
      Object.prototype.toString.call(data) === '[object RegExp]'
    ) {
      return data;
    }

    let output: any[] | any;
    let i = 0;
    let l = 0;
    if (Array.isArray(data)) {
      output = [];
      l = data.length;
      while (i < l) {
        output.push(this.processKeys(convert, data[i], options));
        i++;
      }
    } else {
      output = {};
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          if (options && options.ignoreProperties && options.ignoreProperties.indexOf(key) !== -1) {
            // @ts-ignore
            output[key] = data[key];
          } else {
            // @ts-ignore
            output[convert(key)] = this.processKeys(convert, data[key], options);
          }
        }
      }
    }
    return output;
  }

  private static recursiveFillInReferences(
    items: any[] | object,
    referencesById: { [key: string]: any },
    referencesTypes: string[],
    step: number = 1,
  ): any {
    if (!Array.isArray(items)) {
      return this.recursiveFillInReferences([items], referencesById, referencesTypes, step)[0];
    }

    if (step > 3) {
      return items;
    }

    // Iterate over every item in the collection
    return items.map(item => {
      // If it's a object, lets create a copy of this item and work on that instead
      if (typeof item === 'object' && item !== null) {
        const newItem: Record<string, any> = { ...item };

        // has_many relations references
        [
          ['adminsIds', 'admins', 'Admin'],
          ['adminIds', 'admins', 'Admin'],
          ['attendantIds', 'attendants', 'Admin'],
          ['candidateIds', 'candidates', 'Candidate'],
          ['referralOfferAssignmentsIds', 'referralOfferAssignment', 'ReferralOfferAssignment'],
          ['referralReferrersIds', 'referralReferrers', 'ReferralReferrer'],
        ].forEach(
          // .adminsIds -> .admins // .adminIds -> .admins // .attendantIds -> .attendants // .candidateIds -> .candidates
          ([relationIdsAttribute, relationAttribute, referenceType]) => {
            const referenceIds: any[] = newItem[relationIdsAttribute];
            // eslint-disable-next-line eqeqeq
            if (referenceIds != undefined) {
              const references = referenceIds
                .map(referenceId => referencesById[`${camelCase(referenceType)}_${referenceId}`])
                .filter(x => !!x);
              newItem[relationAttribute] = references;
            }
          },
        );

        referencesTypes.forEach(referenceType => {
          let referenceId = newItem[`${referenceType}Id`];
          let reference = referencesById[`${referenceType}_${referenceId}`];

          // Add reference (if it has been found)
          if (referenceId && reference) {
            newItem[referenceType] = reference;
          }

          // Add polymorphic references (if found)
          ['activatable', 'uploader'].forEach(relation => {
            referenceId = newItem[`${relation}Id`];
            // eslint-disable-next-line no-param-reassign
            referenceType = (newItem[`${relation}Type`] || '').toLowerCase();
            reference = referencesById[`${referenceType}_${referenceId}`];
            if (referenceId && referenceType && reference) {
              newItem[relation] = reference;
            }
          });

          // Recursively iterate over all the other attributes/references of `item`
          for (const key in newItem) {
            if (newItem.hasOwnProperty(key)) {
              const value = newItem[key];
              if (typeof value === 'object' || Array.isArray(value)) {
                newItem[key] = this.recursiveFillInReferences(
                  value,
                  referencesById,
                  referencesTypes,
                  step + 1,
                );
              }
            }
          }
        });

        // remove reference for  admins for visibility
        if (newItem && newItem.visibility) {
          delete newItem.visibility.admins;
        }

        return newItem;
        // If not, just return the item as it was
      } else {
        return item;
      }
    });
  }
}
